import type { CriteriaGroup, CriteriaRoot, CriteriaRule } from './types';

export function createEmptyRule(): CriteriaRule {
  return {
    Hash: null,
    EqualityOperatorType: null,
    Field: null,
    Value: '',
    Name: null,
    SubCriteria: null,
  };
}

export function createEmptyGroup(): CriteriaGroup {
  return {
    Hash: null,
    EqualityOperatorType: null,
    Field: null,
    Value: null,
    Name: null,
    SubCriteria: {
      LogicalOperatorType: null,
      Conditions: [createEmptyRule()],
    },
  };
}

export function createEmptyCriteriaRoot(defaultRootOperator: string): CriteriaRoot {
  return {
    LogicalOperatorType: defaultRootOperator,
    Conditions: [createEmptyGroup()],
  };
}

export function normalizeCriteriaRoot(
  value: CriteriaRoot | null | undefined,
  defaultRootOperator: string,
  _defaultGroupOperator?: string,
): CriteriaRoot {
  if (value == null || !Array.isArray(value.Conditions) || value.Conditions.length === 0) {
    return createEmptyCriteriaRoot(defaultRootOperator);
  }

  const next = structuredClone(value);
  if (next.LogicalOperatorType == null || next.LogicalOperatorType === '') {
    next.LogicalOperatorType = defaultRootOperator;
  }

  next.Conditions = next.Conditions.map((group) => {
    const sub = group.SubCriteria ?? { LogicalOperatorType: null, Conditions: [] };
    const rules =
      Array.isArray(sub.Conditions) && sub.Conditions.length > 0
        ? sub.Conditions
        : [createEmptyRule()];
    return {
      ...group,
      SubCriteria: {
        ...sub,
        LogicalOperatorType: sub.LogicalOperatorType ?? null,
        Conditions: rules,
      },
    };
  });

  return next;
}

function updateGroup(
  root: CriteriaRoot,
  groupIndex: number,
  updater: (group: CriteriaGroup) => CriteriaGroup,
): CriteriaRoot {
  const next = structuredClone(root);
  next.Conditions[groupIndex] = updater(next.Conditions[groupIndex]!);
  return next;
}

export function setGroupOperator(
  root: CriteriaRoot,
  groupIndex: number,
  logicalOperator: string,
): CriteriaRoot {
  return updateGroup(root, groupIndex, (group) => ({
    ...group,
    SubCriteria: {
      ...group.SubCriteria,
      LogicalOperatorType: logicalOperator,
    },
  }));
}

export function addGroup(root: CriteriaRoot): CriteriaRoot {
  const next = structuredClone(root);
  next.Conditions.push(createEmptyGroup());
  return next;
}

export function removeGroup(root: CriteriaRoot, groupIndex: number): CriteriaRoot {
  if (root.Conditions.length <= 1) return root;
  const next = structuredClone(root);
  next.Conditions.splice(groupIndex, 1);
  return next;
}

export function addRule(root: CriteriaRoot, groupIndex: number): CriteriaRoot {
  return updateGroup(root, groupIndex, (group) => ({
    ...group,
    SubCriteria: {
      ...group.SubCriteria,
      Conditions: [...group.SubCriteria.Conditions, createEmptyRule()],
    },
  }));
}

export function removeRule(root: CriteriaRoot, groupIndex: number, ruleIndex: number): CriteriaRoot {
  return updateGroup(root, groupIndex, (group) => {
    const rules = [...group.SubCriteria.Conditions];
    if (rules.length <= 1) return group;
    rules.splice(ruleIndex, 1);
    return {
      ...group,
      SubCriteria: { ...group.SubCriteria, Conditions: rules },
    };
  });
}

/** Legacy: multi-rule → remove row; single rule → reset to empty rule. */
export function removeRuleWithLegacyReset(
  root: CriteriaRoot,
  groupIndex: number,
  ruleIndex: number,
): CriteriaRoot {
  return updateGroup(root, groupIndex, (group) => {
    const rules = [...group.SubCriteria.Conditions];
    if (rules.length > 1) {
      rules.splice(ruleIndex, 1);
      return {
        ...group,
        SubCriteria: { ...group.SubCriteria, Conditions: rules },
      };
    }
    return {
      ...group,
      SubCriteria: { ...group.SubCriteria, Conditions: [createEmptyRule()] },
    };
  });
}

export function updateRuleField(
  root: CriteriaRoot,
  groupIndex: number,
  ruleIndex: number,
  fieldCode: string | null,
): CriteriaRoot {
  return updateGroup(root, groupIndex, (group) => {
    const rules = group.SubCriteria.Conditions.map((rule, idx) => {
      if (idx !== ruleIndex) return rule;
      return {
        ...rule,
        Field: fieldCode,
        EqualityOperatorType: null,
        Value: '',
        Name: null,
      };
    });
    return { ...group, SubCriteria: { ...group.SubCriteria, Conditions: rules } };
  });
}

export function updateRuleOperator(
  root: CriteriaRoot,
  groupIndex: number,
  ruleIndex: number,
  operator: string | null,
): CriteriaRoot {
  return updateGroup(root, groupIndex, (group) => {
    const rules = group.SubCriteria.Conditions.map((rule, idx) =>
      idx === ruleIndex ? { ...rule, EqualityOperatorType: operator } : rule,
    );
    return { ...group, SubCriteria: { ...group.SubCriteria, Conditions: rules } };
  });
}

export function updateRuleValue(
  root: CriteriaRoot,
  groupIndex: number,
  ruleIndex: number,
  value: unknown,
  name?: string | null,
): CriteriaRoot {
  return updateGroup(root, groupIndex, (group) => {
    const rules = group.SubCriteria.Conditions.map((rule, idx) =>
      idx === ruleIndex
        ? { ...rule, Value: value, ...(name !== undefined ? { Name: name } : {}) }
        : rule,
    );
    return { ...group, SubCriteria: { ...group.SubCriteria, Conditions: rules } };
  });
}

export function resolveFieldEntry(
  fieldCode: string | null | undefined,
  fieldCatalog: Array<{ code: string } & Record<string, unknown>>,
): (typeof fieldCatalog)[number] | undefined {
  if (fieldCode == null || String(fieldCode).trim() === '') return undefined;
  return fieldCatalog.find((entry) => entry.code === fieldCode);
}

export function operatorsForField(
  configOperators: Array<{ code: string; label: string }>,
  entry?: { operators?: string[] | 'inherit' },
): Array<{ code: string; label: string }> {
  if (!entry || entry.operators === 'inherit' || entry.operators == null) {
    return configOperators;
  }
  const allowed = new Set(entry.operators);
  return configOperators.filter((op) => allowed.has(op.code));
}
